import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Boxes, CalendarClock, PackageCheck } from "lucide-react";
import { api } from "@/lib/api";

const Kpi = ({ label, value, sub, tone, testid }) => (
  <div className="rounded-md border border-[#E5E7EB] bg-white p-4" data-testid={testid}>
    <p className="text-[10px] uppercase tracking-[0.12em] text-[#6B7280]">{label}</p>
    <p className="font-heading text-3xl font-extrabold mt-1" style={{ color: tone }}>{value}</p>
    <p className="text-xs text-[#6B7280] mt-0.5">{sub}</p>
  </div>
);

const BUCKET_TONE = { Adequate: "#2E7D32", Reorder: "#B45309", Critical: "#C92A2A", "Out of Stock": "#374151" };

export default function InventoryOverview() {
  const [d, setD] = useState(null);

  useEffect(() => { api.get("/store/overview").then(({ data }) => setD(data)).catch(() => setD(null)); }, []);

  if (!d) return <p className="text-sm text-[#6B7280]" data-testid="overview-loading">Loading inventory overview…</p>;

  const total = Object.values(d.buckets).reduce((a, b) => a + b, 0) || 1;

  return (
    <div data-testid="inventory-overview-page">
      <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-[#111827]">Inventory Overview</h1>
      <p className="text-sm text-[#6B7280] mt-1">Live stock health for the haematology sub-store, calculated from the ledger.</p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        <Kpi label="Active items" value={d.active_items} sub="In the item master" tone="#111827" testid="kpi-active-items" />
        <Kpi label="Critical / out of stock" value={d.buckets.Critical + d.buckets["Out of Stock"]} sub="At or below minimum, or nil" tone="#C92A2A" testid="kpi-critical-stock" />
        <Kpi label="Reorder now" value={d.reorder_now} sub="At or below reorder level" tone="#B45309" testid="kpi-reorder-now" />
        <Kpi label="Spend (30 days)" value={d.spend_30d} sub="Value of receipts recorded" tone="#002FA7" testid="kpi-spend" />
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mt-4">
        <div className="rounded-md border border-[#E5E7EB] bg-white p-4" data-testid="stock-health">
          <h2 className="font-heading font-bold text-sm text-[#111827] flex items-center gap-2">
            <Boxes className="h-4 w-4 text-[#002FA7]" /> Live stock health
          </h2>
          <div className="mt-3 space-y-2">
            {Object.entries(d.buckets).map(([k, v]) => (
              <div key={k} data-testid={`bucket-${k}`}>
                <div className="flex justify-between text-xs">
                  <span className="text-[#111827]">{k}</span>
                  <span className="text-[#6B7280]">{v}</span>
                </div>
                <div className="h-2 rounded-full bg-[#F3F4F6] mt-1 overflow-hidden">
                  <div className="h-full rounded-full transition-all"
                    style={{ width: `${(v / total) * 100}%`, backgroundColor: BUCKET_TONE[k] }} />
                </div>
              </div>
            ))}
          </div>
          <Link to="/store/items" className="inline-block mt-4 text-xs text-[#002FA7] hover:underline"
            data-testid="overview-items-link">View item master →</Link>
        </div>

        <div className="rounded-md border border-[#E5E7EB] bg-white p-4" data-testid="by-section">
          <h2 className="font-heading font-bold text-sm text-[#111827]">Items by laboratory section</h2>
          <div className="mt-3 space-y-2">
            {d.by_section.map((s) => (
              <div key={s.section} className="flex items-center gap-2" data-testid={`section-${s.section}`}>
                <span className="text-xs text-[#111827] w-56 truncate" title={s.section}>{s.section}</span>
                <div className="flex-1 h-2 rounded-full bg-[#F3F4F6] overflow-hidden">
                  <div className="h-full bg-[#002FA7] rounded-full"
                    style={{ width: `${(s.count / d.active_items) * 100}%` }} />
                </div>
                <span className="text-xs text-[#6B7280] w-6 text-right">{s.count}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-md border border-[#E5E7EB] bg-white p-4" data-testid="reorder-list">
          <h2 className="font-heading font-bold text-sm text-[#111827] flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-[#B45309]" /> Reorder now
          </h2>
          {d.reorder_list.length === 0
            ? <p className="text-sm text-[#6B7280] mt-3">Nothing needs reordering.</p>
            : (
              <div className="mt-3 divide-y divide-[#E5E7EB]">
                {d.reorder_list.map((i) => (
                  <div key={i.code} className="py-2 flex items-center justify-between text-xs">
                    <span className="text-[#111827] truncate max-w-[60%]" title={i.generic_name}>{i.generic_name}</span>
                    <span className="text-[#6B7280]">{i.on_hand} {i.stock_unit} · need {i.suggested_qty}</span>
                  </div>
                ))}
              </div>
            )}
          <Link to="/store/indents" className="inline-block mt-3 text-xs text-[#002FA7] hover:underline"
            data-testid="overview-indent-link">Open indent planner →</Link>
        </div>

        <div className="rounded-md border border-[#E5E7EB] bg-white p-4" data-testid="expiry-watch">
          <h2 className="font-heading font-bold text-sm text-[#111827] flex items-center gap-2">
            <CalendarClock className="h-4 w-4 text-[#B45309]" /> Expiry watch (90 days)
          </h2>
          {d.expired.length > 0 && (
            <p className="text-xs text-[#C92A2A] mt-2" data-testid="expired-count">
              {d.expired.length} expired lot(s) still on the shelf — discard and record the movement.
            </p>
          )}
          {d.expiry_watch.length === 0 && d.expired.length === 0
            ? <p className="text-sm text-[#6B7280] mt-3">No lots expiring in the next 90 days.</p>
            : (
              <div className="mt-3 divide-y divide-[#E5E7EB]">
                {[...d.expired, ...d.expiry_watch].map((lo, idx) => (
                  <div key={`${lo.code}-${lo.lot_number}-${idx}`} className="py-2 flex items-center justify-between text-xs">
                    <span className="text-[#111827] truncate max-w-[55%]" title={lo.item}>{lo.item}</span>
                    <span className="text-[#6B7280]">lot {lo.lot_number} · {lo.expiry_date}</span>
                  </div>
                ))}
              </div>
            )}
        </div>

        <div className="rounded-md border border-[#E5E7EB] bg-white p-4 lg:col-span-2" data-testid="top-consumption">
          <h2 className="font-heading font-bold text-sm text-[#111827] flex items-center gap-2">
            <PackageCheck className="h-4 w-4 text-[#002FA7]" /> Highest consumption (30 days)
          </h2>
          {d.top_consumption.length === 0
            ? <p className="text-sm text-[#6B7280] mt-3">No issues or consumption recorded yet.</p>
            : (
              <div className="mt-3 grid sm:grid-cols-2 gap-2">
                {d.top_consumption.map((c) => (
                  <div key={c.item} className="flex items-center justify-between text-xs border border-[#E5E7EB] rounded px-3 py-2">
                    <span className="text-[#111827] truncate max-w-[70%]" title={c.item}>{c.item}</span>
                    <span className="text-[#6B7280]">{c.quantity}</span>
                  </div>
                ))}
              </div>
            )}
        </div>
      </div>
    </div>
  );
}
